import { z } from 'zod';
import { ItemSchema } from './equipment';

/**
 * Una singola opzione all'interno di un gruppo di scelta dell'equipaggiamento
 * iniziale (es. "Ascia (media, peso 1)" è un'opzione del gruppo "Scegli la
 * tua arma").
 *
 * Un'opzione concede UNO o PIÙ oggetti contemporaneamente: ad esempio
 * l'opzione "Attrezzatura da Avventuriero + Razioni" è un singolo blocco
 * con due item.
 */
export const EquipmentChoiceOptionSchema = z.object({
  /** ID stabile dell'opzione entro il gruppo. */
  id: z.string().min(1).max(50),
  /** Etichetta sintetica per la UI (es. 'Ascia', 'Maglia di ferro'). */
  label: z.string().min(1).max(100),
  /** Item ottenuti se questa opzione è scelta. */
  items: z.array(ItemSchema).min(1),
  /** Descrizione narrativa opzionale (lore, restrizioni). */
  description: z.string().optional(),
});
export type EquipmentChoiceOption = z.infer<typeof EquipmentChoiceOptionSchema>;

/**
 * Un gruppo di scelta dell'equipaggiamento iniziale.
 *
 * Il giocatore deve scegliere `pickCount` opzioni all'interno del gruppo
 * (default 1, valido per la maggior parte dei casi). Il manuale presenta
 * frasi tipo "Scegli la tua arma:" (1 di N) o "Scegli **due** opzioni tra le
 * seguenti:" (2 di N, es. equipaggiamento extra del Guerriero, manuale V1
 * r. 3373).
 */
export const EquipmentChoiceGroupSchema = z.object({
  /** ID stabile del gruppo (es. 'arma-iniziale', 'attrezzatura-vs-armatura'). */
  id: z.string().min(1).max(50),
  /** Testo della scelta presentato al giocatore. */
  prompt: z.string().min(1).max(200),
  options: z.array(EquipmentChoiceOptionSchema).min(2),
  /**
   * Quante opzioni il giocatore deve scegliere all'interno del gruppo.
   * Default 1 (valido per Ladro e maggior parte delle classi). Il Guerriero
   * usa `pickCount: 2` per il gruppo dell'extra iniziale.
   */
  pickCount: z.number().int().min(1).default(1),
});
export type EquipmentChoiceGroup = z.infer<typeof EquipmentChoiceGroupSchema>;

/**
 * Equipaggiamento iniziale completo della classe:
 *  - una lista di item SEMPRE concessi (`always`)
 *  - zero o più gruppi di scelta da compilare
 *
 * Esempio Barbaro:
 *  - always: [Razioni, Pugnale, Ricordo]
 *  - choices: [{ "Scegli la tua arma" → [Ascia | Spada lunga] },
 *              { "Scegli un'opzione" → [Attrezzatura+Razioni | Maglia di ferro] }]
 */
export const StartingEquipmentSchema = z.object({
  /** Item ricevuti automaticamente da tutti i PG di questa classe. */
  always: z.array(ItemSchema).default([]),
  /** Scelte aggiuntive che il giocatore deve risolvere alla creazione PG. */
  choices: z.array(EquipmentChoiceGroupSchema).default([]),
});
export type StartingEquipment = z.infer<typeof StartingEquipmentSchema>;
